/**
 * Cumulative S-curve: actual vs the four forecast scenarios.
 * `metric="enrolled"` -> Enrollment S-curve; `metric="screened"` -> Screening S-curve.
 */
import { useCallback } from "react";
import * as d3 from "d3";
import {
  COLORS,
  SCENARIOS,
  useD3Chart,
  drawTimeAxes,
  drawCutoff,
  parseMonth,
  FONT,
  type Margin,
} from "./chartBase";
import { buildEnrollmentForecastSeries } from "./enrollmentForecast";
import type {
  EnrollmentComparisonChartProps,
  OnboardingMonthRow,
} from "./onboardingPlots.types";

export default function EnrollmentComparisonChart({
  rows,
  metric = "enrolled",
  height = 380,
  sparseXTicks = false,
}: EnrollmentComparisonChartProps) {
  const draw = useCallback(
    (svg, tip, W) => {
      if (!rows.length) return;
      const H = height;
      const m: Margin = { top: 16, right: 20, bottom: 34, left: 44 };
      const HOVER_TOLERANCE = 14;

      const actualOf = (r: OnboardingMonthRow) =>
        metric === "enrolled" ? r.enrolledCumulative : r.screenedCumulative;
      const scenOf = (
        r: OnboardingMonthRow,
        key: keyof OnboardingMonthRow["scenarios"],
      ) =>
        metric === "enrolled"
          ? r.scenarios[key].enrolledCum
          : r.scenarios[key].screenedCum;

      const pts = rows.map((r) => ({ ...r, _d: parseMonth(r.date) as Date }));
      const cutoff = [...pts].reverse().find((r) => actualOf(r) !== null);
      if (!cutoff) return;
      const forecast =
        metric === "enrolled" ? buildEnrollmentForecastSeries(rows) : null;
      const forecastPts = (forecast?.points ?? []).map((point) => ({
        ...point,
        _d: parseMonth(point.date) as Date,
      }));
      const forecastByDate = new Map(
        forecastPts.map((point) => [point.date, point] as const),
      );
      const forecastLine = forecastPts.filter((r) => r.p50 != null);
      const forecastBand = forecastPts.filter(
        (r) => r.p10 != null && r.p90 != null,
      );
      const scenarioKeys = SCENARIOS.map((s) => s.key);

      const x = d3
        .scaleTime()
        .domain(d3.extent(pts, (r) => r._d) as [Date, Date])
        .range([m.left, W - m.right]);
      const yMax =
        d3.max(pts, (r) =>
          d3.max(
            [actualOf(r), ...SCENARIOS.map((s) => scenOf(r, s.key))].filter(
              (v) => v != null,
            ) as number[],
          ),
        ) ?? 10;
      const forecastMax =
        metric === "enrolled" && forecastLine.length
          ? d3.max(forecastPts, (r) =>
              d3.max(
                [r.p10, r.p50, r.p90].filter((v) => v != null) as number[],
              ),
            )
          : null;
      const y = d3
        .scaleLinear()
        .domain([0, Math.max(10, yMax, forecastMax ?? 0)])
        .nice()
        .range([H - m.bottom, m.top]);

      drawTimeAxes(svg, W, H, m, x, y, undefined, sparseXTicks);

      // band: minimum .. stretch
      const band = pts.filter(
        (r) => scenOf(r, "stretch") != null && scenOf(r, "minimum") != null,
      );
      svg
        .append("path")
        .datum(band)
        .attr("fill", COLORS.actual)
        .attr("opacity", 0.06)
        .attr(
          "d",
          d3
            .area<any>()
            .x((r) => x(r._d))
            .y0((r) => y(scenOf(r, "minimum") as number))
            .y1((r) => y(scenOf(r, "stretch") as number)),
        );

      SCENARIOS.forEach((s) => {
        svg
          .append("path")
          .datum(pts)
          .attr("fill", "none")
          .attr("stroke", s.color)
          .attr("stroke-width", 1.8)
          .attr("stroke-dasharray", "6 4")
          .attr("stroke-linecap", "round")
          .attr("stroke-linejoin", "round")
          .attr("opacity", 0.9)
          .attr(
            "d",
            d3
              .line<any>()
              .defined((r) => scenOf(r, s.key) != null)
              .x((r) => x(r._d))
              .y((r) => y(scenOf(r, s.key) as number)),
          );
      });

      drawCutoff(svg, cutoff._d, m, H, x);

      svg
        .append("path")
        .datum(pts)
        .attr("fill", "none")
        .attr("stroke", COLORS.actual)
        .attr("stroke-width", 3.5)
        .attr("stroke-linecap", "round")
        .attr("stroke-linejoin", "round")
        .attr(
          "d",
          d3
            .line<any>()
            .defined((r) => actualOf(r) != null)
            .x((r) => x(r._d))
            .y((r) => y(actualOf(r) as number)),
        );
      if (metric === "enrolled" && forecastLine.length) {
        if (forecastBand.length) {
          svg
            .append("path")
            .datum(forecastBand)
            .attr("fill", COLORS.forecastFill)
            .attr(
              "d",
              d3
                .area<any>()
                .x((r) => x(r._d))
                .y0((r) => y(r.p10 as number))
                .y1((r) => y(r.p90 as number)),
            );
        }

        svg
          .append("path")
          .datum(forecastLine)
          .attr("fill", "none")
          .attr("stroke", COLORS.forecast)
          .attr("stroke-width", 3)
          .attr("stroke-linecap", "round")
          .attr("stroke-linejoin", "round")
          .attr(
            "d",
            d3
              .line<any>()
              .defined((r) => r.p50 != null)
              .x((r) => x(r._d))
              .y((r) => y(r.p50 as number)),
          );

        svg
          .selectAll("circle.forecast")
          .data(forecastLine.slice(1))
          .join("circle")
          .attr("class", "forecast")
          .attr("cx", (r: any) => x(r._d))
          .attr("cy", (r: any) => y(r.p50 as number))
          .attr("r", 3)
          .attr("fill", COLORS.forecast);
      }
      svg
        .selectAll("circle.actual")
        .data(pts.filter((r) => actualOf(r) != null))
        .join("circle")
        .attr("cx", (r: any) => x(r._d))
        .attr("cy", (r: any) => y(actualOf(r) as number))
        .attr("r", 3.5)
        .attr("fill", COLORS.actual);

      svg
        .append("rect")
        .attr("x", m.left)
        .attr("y", m.top)
        .attr("width", W - m.left - m.right)
        .attr("height", H - m.top - m.bottom)
        .attr("fill", "transparent")
        .on("mousemove", function (ev: MouseEvent) {
          const [mx, my] = d3.pointer(ev, this);
          const dt = x.invert(mx);
          const r = pts.reduce((p, c) =>
            Math.abs(+c._d - +dt) < Math.abs(+p._d - +dt) ? c : p,
          );
          const actualValue = actualOf(r);
          const actualY = actualValue != null ? y(actualValue) : null;
          const forecastRow =
            metric === "enrolled" ? forecastByDate.get(r.date) : null;
          const forecastValue = forecastRow?.p50;
          const forecastY =
            metric === "enrolled" && forecastValue != null
              ? y(forecastValue)
              : null;
          const scenarioYs = scenarioKeys
            .map((key) => scenOf(r, key))
            .filter((value): value is number => value != null)
            .map((value) => y(value));
          const isNearActual =
            actualY != null && Math.abs(my - actualY) <= HOVER_TOLERANCE;
          const isNearForecast =
            forecastY != null && Math.abs(my - forecastY) <= HOVER_TOLERANCE;
          const isNearScenario = scenarioYs.some(
            (scenarioY) => Math.abs(my - scenarioY) <= HOVER_TOLERANCE,
          );
          if (!isNearActual && !isNearForecast && !isNearScenario) {
            tip.hide();
            return;
          }
          const label = metric === "enrolled" ? "Enrolled" : "Screened";
          const forecastText =
            metric === "enrolled" && forecastRow?.p50 != null
              ? `<br>Forecast: <b>${forecastRow.p50}</b>${
                  forecastRow.p10 != null && forecastRow.p90 != null
                    ? ` (${forecastRow.p10}–${forecastRow.p90})`
                    : ""
                }`
              : "";
          tip.show(
            `<b>${r.date}</b><br>Actual ${label}: <b>${actualValue ?? "–"}</b><br>` +
              forecastText +
              SCENARIOS.map(
                (s) => `${s.label}: ${scenOf(r, s.key) ?? "–"}`,
              ).join("<br>"),
            ev,
          );
        })
        .on("mouseleave", () => tip.hide());
    },
    [rows, metric, height, sparseXTicks],
  );
  const { ref, svgRef, width } = useD3Chart(draw);

  return (
    <div
      ref={ref}
      style={{ position: "relative", width: "100%", fontFamily: FONT }}
    >
      <svg ref={svgRef} width={width} height={height} />
    </div>
  );
}
